import { useState, useRef, useEffect, useMemo } from 'react'

/**
 * Combobox de búsqueda de cuentas contables Zeta.
 *
 * Reemplaza <select> nativos cuando hay ~100+ cuentas: permite tipear código
 * o parte del nombre y filtra en tiempo real.
 *
 * Props:
 *   value       - código de cuenta seleccionado (string)
 *   onChange    - fn(codigo) al seleccionar
 *   cuentas     - array [{ codigo, nombre, tipo?, es_imputable? }]
 *                 (esquema tabla cuentas_contables)
 *   placeholder
 *   size        - 'sm' | 'md' (default sm — para tablas densas)
 *   disabled
 *   soloImputables - default true. Solo muestra hojas (es_imputable=true).
 *                    Los grupos (nivel 1-5) no pueden recibir asientos.
 *
 * Keyboard: ↓↑ navegar, Enter seleccionar, Esc cerrar, Tab confirma+cierra.
 * Click fuera cierra sin cambiar.
 */
export default function SelectCuenta({
  value,
  onChange,
  cuentas = [],
  placeholder = '— elegir cuenta —',
  size = 'sm',
  disabled = false,
  soloImputables = true,
}) {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [highlighted, setHighlighted] = useState(0)
  const wrapperRef = useRef(null)
  const inputRef = useRef(null)
  const listRef = useRef(null)

  // Filtro base: si soloImputables=true, quedan las hojas
  const cuentasBase = useMemo(() => {
    if (!soloImputables) return cuentas
    return cuentas.filter(c => c.es_imputable !== false)
  }, [cuentas, soloImputables])

  const selected = useMemo(
    () => cuentas.find(c => c.codigo === value) || null,
    [value, cuentas]
  )

  const display = selected
    ? `${selected.codigo} · ${selected.nombre}`
    : ''

  const filtered = useMemo(() => {
    if (!query.trim()) return cuentasBase
    const q = query.trim().toLowerCase()
    // Prioridad: código startsWith > código contains > nombre contains
    const starts = []
    const contains = []
    const nameOnly = []
    for (const c of cuentasBase) {
      const cod = String(c.codigo).toLowerCase()
      const nom = String(c.nombre).toLowerCase()
      if (cod.startsWith(q)) starts.push(c)
      else if (cod.includes(q)) contains.push(c)
      else if (nom.includes(q)) nameOnly.push(c)
    }
    return [...starts, ...contains, ...nameOnly]
  }, [query, cuentasBase])

  useEffect(() => {
    if (highlighted >= filtered.length) setHighlighted(Math.max(0, filtered.length - 1))
  }, [filtered, highlighted])

  // Click afuera → cerrar
  useEffect(() => {
    if (!open) return
    function onDocClick(e) {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false)
        setQuery('')
      }
    }
    document.addEventListener('mousedown', onDocClick)
    return () => document.removeEventListener('mousedown', onDocClick)
  }, [open])

  // Scroll al highlighted
  useEffect(() => {
    if (!open || !listRef.current) return
    const el = listRef.current.querySelector(`[data-idx="${highlighted}"]`)
    if (el) el.scrollIntoView({ block: 'nearest' })
  }, [highlighted, open])

  function pick(c) {
    onChange(c.codigo)
    setQuery('')
    setOpen(false)
    inputRef.current?.blur()
  }

  function onKeyDown(e) {
    if (!open) {
      if (e.key === 'ArrowDown' || e.key === 'Enter') {
        e.preventDefault()
        setOpen(true)
        setHighlighted(0)
      }
      return
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setHighlighted(h => Math.min(filtered.length - 1, h + 1))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setHighlighted(h => Math.max(0, h - 1))
    } else if (e.key === 'Enter') {
      e.preventDefault()
      if (filtered[highlighted]) pick(filtered[highlighted])
    } else if (e.key === 'Escape') {
      e.preventDefault()
      setOpen(false)
      setQuery('')
    } else if (e.key === 'Tab') {
      if (query && filtered[highlighted]) {
        pick(filtered[highlighted])
      } else {
        setOpen(false)
        setQuery('')
      }
    }
  }

  const pad = size === 'md' ? '7px 10px' : '4px 6px'
  const fontSize = size === 'md' ? 12 : 11

  const inputStyle = {
    width: '100%',
    padding: pad,
    border: '1px solid #cbd5e1',
    borderRadius: 6,
    fontSize,
    color: '#1a3461',
    fontFamily: 'inherit',
    boxSizing: 'border-box',
    background: disabled ? '#f8fafc' : 'white',
    cursor: disabled ? 'not-allowed' : 'text',
    outline: 'none',
  }

  return (
    <div ref={wrapperRef} style={{ position: 'relative', width: '100%' }}>
      <input
        ref={inputRef}
        type="text"
        value={open ? query : display}
        onChange={e => {
          setQuery(e.target.value)
          if (!open) setOpen(true)
          setHighlighted(0)
        }}
        onFocus={() => {
          if (!disabled) setOpen(true)
        }}
        onKeyDown={onKeyDown}
        placeholder={placeholder}
        disabled={disabled}
        style={inputStyle}
        autoComplete="off"
        spellCheck={false}
      />
      {open && (
        <div
          ref={listRef}
          style={{
            position: 'absolute',
            top: 'calc(100% + 2px)',
            left: 0,
            right: 0,
            maxHeight: 260,
            overflowY: 'auto',
            background: 'white',
            border: '1px solid #cbd5e1',
            borderRadius: 6,
            boxShadow: '0 6px 18px rgba(15, 23, 42, 0.12)',
            zIndex: 1000,
            fontSize,
          }}
        >
          {filtered.length === 0 && (
            <div style={{ padding: '8px 10px', color: '#94a3b8', fontStyle: 'italic' }}>
              Sin coincidencias — probá con otro código o nombre
            </div>
          )}
          {filtered.map((c, i) => {
            const isHighlighted = i === highlighted
            const isSelected = c.codigo === value
            return (
              <div
                key={c.codigo}
                data-idx={i}
                onMouseDown={e => {
                  e.preventDefault()
                  pick(c)
                }}
                onMouseEnter={() => setHighlighted(i)}
                style={{
                  padding: '5px 10px',
                  cursor: 'pointer',
                  background: isHighlighted ? '#e0f2fe' : isSelected ? '#f0f9ff' : 'white',
                  color: '#1a3461',
                  display: 'flex',
                  gap: 8,
                  alignItems: 'baseline',
                  borderBottom: '1px solid #f1f5f9',
                }}
              >
                <span
                  style={{
                    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                    color: '#64748b',
                    fontWeight: 700,
                    minWidth: 60,
                  }}
                >
                  {c.codigo}
                </span>
                <span style={{ flex: 1 }}>{c.nombre}</span>
                {c.tipo && (
                  <span
                    style={{
                      fontSize: fontSize - 1,
                      color: '#94a3b8',
                      textTransform: 'uppercase',
                      letterSpacing: 0.4,
                    }}
                  >
                    {c.tipo}
                  </span>
                )}
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
